import { useCallback, useEffect, useRef } from "react";

// Simple hook to detect inside / outside clicks relative to a ref; Optional callbacks; Returns width & height of ref via resizeDetector

interface UseIsClickedOutsideProps<T extends Node> {
  onClickInside?: (ref: T, target: EventTarget | null) => void;
  onClickOutside?: (ref: T, target: EventTarget | null) => void;
}
/**
 * Custom React hook to detect clicks inside or outside a referenced DOM node.
 * @author @CorradoSurmanowicz
 * This hook provides a `ref` to be attached to a DOM element and calls the provided
 * callback functions when a click event occurs inside or outside the referenced element.
 *
 * @template T - The type of the DOM node (must extend `Node`).
 * @param props - The properties object.
 * @param props.onClickInside - Optional callback invoked when a click occurs inside the referenced element.
 * @param props.onClickOutside - Optional callback invoked when a click occurs outside the referenced element.
 * @returns An object containing the `ref` to be attached to the target element.
 *
 * @example
 * ```tsx
 * const { ref } = useIsRefClicked<HTMLDivElement>({
 *   onClickInside: () => console.log('Clicked inside!'),
 *   onClickOutside: () => console.log('Clicked outside!'),
 * });
 *
 * return <div ref={ref}>Click me!</div>;
 * ```
 */
export const useIsRefClicked = <T extends Node>({ onClickInside, onClickOutside }: UseIsClickedOutsideProps<T>) => {
  // const { ref, width, height } = useResizeDetector<T>();
  const ref = useRef<T | null>(null);

  const handleClickOutside = useCallback(
    (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        onClickOutside?.(ref.current, event.target);
      }
    },
    [ref, onClickOutside]
  );
  const handleClickInside = useCallback(
    (event: MouseEvent) => {
      if (ref.current && ref.current.contains(event.target as Node)) {
        onClickInside?.(ref.current, event.target);
      }
    },
    [ref, onClickInside]
  );

  useEffect(() => {
    if (onClickOutside) document.addEventListener("click", handleClickOutside);
    if (onClickInside) document.addEventListener("click", handleClickInside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
      document.removeEventListener("click", handleClickInside);
    };
  }, [handleClickInside, handleClickOutside, onClickInside, onClickOutside]);

  return { ref };
};
